import type {BrowserOptions} from '@sentry/react';

import type {MediaPriority, ReceiverJitterBufferTarget} from '@pexip/infinity';
import {isMediaPriority, isJitterBufferTarget} from '@pexip/infinity';
import {INCLUDE_EXCLUDE_CONSTRAINT} from '@pexip/media-control';
import type {IncludeExcludeConstraint} from '@pexip/media-control';
import type {LogLevelsString} from '@pexip/logger';

import type {FeedbackConfig, TestIdValue} from './types';
import {logger, rootLogger} from './logger';
import {
    shouldEnableAudioProcessing,
    shouldEnableVideoProcessing,
} from './config';
import {
    BANDWIDTHS,
    SEGMENTATION_MODEL,
    TERMS_URL,
    BG_IMAGE_URL,
    DEFAULT_FEEDBACK_CONFIG,
    TRANSFER_TIMEOUT_S,
    FRAME_RATE,
    AVAILABLE_CALL_TYPES,
    PRIVACY_URL,
    PRESENTATION_JITTER_BUFFER_TARGET_MS,
} from './constants';
import type {StringQuadruple} from './constants';
import {isClientCallType} from './utils/isClientCallType';
import {getClientCallType} from './utils/getClientCallType';

const el = document.querySelector('#ic-config');

export interface DeploymentConfig {
    sentry?: BrowserOptions;
}

export const deploymentConfig = (() => {
    try {
        return el ? (JSON.parse(el.innerHTML) as DeploymentConfig) : {};
    } catch (error: unknown) {
        logger.error(error, 'failed to parse deploymentConfig');
        return {};
    }
})();

export const applicationConfig = {
    ...deploymentConfig,
    availableCallTypes: AVAILABLE_CALL_TYPES,
    audioProcessing: shouldEnableAudioProcessing(),
    bandwidths: BANDWIDTHS,
    frameRate: FRAME_RATE,
    node: window.location.host,
    segmentationModel: SEGMENTATION_MODEL,
    dynamicVideoProcessingDimensions: false,
    showLiveCaptionsFeature: true,
    videoProcessing: shouldEnableVideoProcessing(),
    shouldMaskConference: false,
    directMedia: true,
    termsAndConditions: {
        en: TERMS_URL,
    } as Record<string, string>,
    privacyPolicy: {
        en: PRIVACY_URL,
    } as Record<string, string>,
    disconnectDestination: '',
    bgImageAssets: [BG_IMAGE_URL],
    showTermsAndConditionsLink: true,
    monitorTypeSurfaces:
        INCLUDE_EXCLUDE_CONSTRAINT.Include as IncludeExcludeConstraint,
    selfBrowserSurface:
        INCLUDE_EXCLUDE_CONSTRAINT.Exclude as IncludeExcludeConstraint,
    systemAudio: INCLUDE_EXCLUDE_CONSTRAINT.Include as IncludeExcludeConstraint,
    hiddenFunctionality: {} as Record<TestIdValue, true>,
    enableFeedback: true,
    feedback: DEFAULT_FEEDBACK_CONFIG,
    /**
     * Same as `networkPriorities` for backward compatibility
     * @see {@link https://gitlab.com/pexip/zoo/-/issues/5042#note_2302767328}
     */
    mediaPriorities: {
        video: 'low',
        audio: 'low',
        presentation: 'low',
    } satisfies MediaPriority as MediaPriority,
    networkPriorities: undefined as MediaPriority | undefined,
    encodingPriorities: {
        video: 'low',
        audio: 'medium',
        presentation: 'medium',
    } satisfies MediaPriority as MediaPriority,
    jitterBufferTarget: {
        presentation: PRESENTATION_JITTER_BUFFER_TARGET_MS,
    } satisfies ReceiverJitterBufferTarget as
        | ReceiverJitterBufferTarget
        | undefined,
    transferTimeout: TRANSFER_TIMEOUT_S,
    logLevel: undefined as undefined | LogLevelsString,
    showMeetingTitleInHeader: true,
};
export type ApplicationConfig = typeof applicationConfig;

export const setApplicationConfig = <K extends keyof ApplicationConfig>(
    key: K,
    value: ApplicationConfig[K],
) => {
    switch (key) {
        case 'encodingPriorities': {
            if (isMediaPriority(value)) {
                applicationConfig.encodingPriorities = value;
            }
            break;
        }
        case 'networkPriorities':
        case 'mediaPriorities': {
            if (isMediaPriority(value)) {
                applicationConfig.networkPriorities = value;
            }
            break;
        }
        case 'jitterBufferTarget': {
            if (isJitterBufferTarget(value)) {
                applicationConfig.jitterBufferTarget = value;
            }
            break;
        }
        case 'bandwidths':
            if (Array.isArray(value)) {
                const accepted = value
                    .flatMap(v => {
                        const n = +v;
                        // 0 is not a valid bandwidth
                        if (n) {
                            return [v];
                        }
                        return [];
                    })
                    .sort((a, b) => +a - +b)
                    .slice(0, BANDWIDTHS.length);
                if (accepted.length === BANDWIDTHS.length) {
                    applicationConfig.bandwidths = accepted as StringQuadruple;
                }
            }
            break;
        case 'feedback': {
            // If the feedback url is not provided, use the default one
            if ((value as FeedbackConfig).url === undefined) {
                applicationConfig.feedback.url = DEFAULT_FEEDBACK_CONFIG.url;
            } else {
                applicationConfig.feedback.url = (value as FeedbackConfig).url;
            }
            // If the enable feedback is not provided, use the default one
            if ((value as FeedbackConfig).enable === undefined) {
                applicationConfig.feedback.enable =
                    DEFAULT_FEEDBACK_CONFIG.enable;
            } else {
                applicationConfig.feedback.enable = (
                    value as FeedbackConfig
                ).enable;
            }
            break;
        }
        case 'logLevel':
            if (value && typeof value === 'string') {
                logger.info('Setting log level to', value);
                rootLogger.level = value;
            }
            applicationConfig[key] = value;
            break;

        case 'availableCallTypes':
            if (Array.isArray(value)) {
                applicationConfig.availableCallTypes = value.flatMap(
                    callType => {
                        if (typeof callType === 'string') {
                            return getClientCallType(callType) ?? [];
                        }
                        return isClientCallType(callType) ? [callType] : [];
                    },
                );
            }
            break;
        default:
            applicationConfig[key] = value;
            break;
    }
};
